// src/app/providers/AuthProvider.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import AuthApi from "../../features/terminal-auth/api/authApi";
import httpClient from "../../shared/api/httpClient";

const AuthContext = createContext(null);

const TOKEN_KEY = "sabi_access_token";
const REFRESH_TOKEN_KEY = "sabi_refresh_token";
const USER_KEY = "sabi_user";
const EMPRESAS_KEY = "sabi_empresa";
const ACCESOS_KEY = "sabi_accesos";
const EMPRESA_ID_KEY = "sabi_empresa_id";
const EMPRESA_NOMBRE_KEY = "sabi_empresa_nombre";
const SUCURSAL_ID_KEY = "sabi_sucursal_id";
const SUCURSAL_NOMBRE_KEY = "sabi_sucursal_nombre";

function safeGetItem(key) {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function licensesToEmpresas(licenses) {
  const list = Array.isArray(licenses) ? licenses : [];
  return list.map((l) => ({
    id: String(l.id),
    name: l.name || `Empresa #${l.id}`,
  }));
}

function resolveCurrentCompany(meRes) {
  const licenses = Array.isArray(meRes?.licenses) ? meRes.licenses : [];

  const licenseId =
    meRes?.context?.licenseId != null ? String(meRes.context.licenseId) : "";

  const backendBranchId =
    meRes?.context?.branchId != null ? String(meRes.context.branchId) : "";

  const matched =
    licenses.find((l) => String(l.id) === licenseId) || null;

  const storedEmpresaId = safeGetItem(EMPRESA_ID_KEY);
  const storedSucursalId = safeGetItem(SUCURSAL_ID_KEY);

  const shouldKeepStoredBranch =
    !backendBranchId &&
    !!licenseId &&
    storedEmpresaId === licenseId &&
    !!storedSucursalId;

  return {
    empresaId: licenseId,
    empresaNombre: matched?.name || "",
    sucursalId: backendBranchId || (shouldKeepStoredBranch ? storedSucursalId : ""),
  };
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem(TOKEN_KEY) || "";
    } catch {
      return "";
    }
  });

  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const [empresas, setEmpresas] = useState(() => {
    try {
      const raw = localStorage.getItem(EMPRESAS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const [accesos, setAccesos] = useState(() => {
    try {
      const raw = localStorage.getItem(ACCESOS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const [loading, setLoading] = useState(true);

  const persist = useCallback(
    (
      nextToken,
      nextRefreshToken,
      nextUser,
      nextEmpresas,
      nextAccesos,
      nextCompany
    ) => {
      try {
        if (nextToken) localStorage.setItem(TOKEN_KEY, nextToken);
        else localStorage.removeItem(TOKEN_KEY);

        if (nextRefreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, nextRefreshToken);
        else localStorage.removeItem(REFRESH_TOKEN_KEY);

        if (nextUser) localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
        else localStorage.removeItem(USER_KEY);

        if (Array.isArray(nextEmpresas)) {
          localStorage.setItem(EMPRESAS_KEY, JSON.stringify(nextEmpresas));
        } else {
          localStorage.removeItem(EMPRESAS_KEY);
        }

        if (Array.isArray(nextAccesos)) {
          localStorage.setItem(ACCESOS_KEY, JSON.stringify(nextAccesos));
        } else {
          localStorage.removeItem(ACCESOS_KEY);
        }

        if (nextCompany?.empresaId) {
          localStorage.setItem(EMPRESA_ID_KEY, nextCompany.empresaId);
        } else {
          localStorage.removeItem(EMPRESA_ID_KEY);
        }

        if (nextCompany?.empresaNombre) {
          localStorage.setItem(EMPRESA_NOMBRE_KEY, nextCompany.empresaNombre);
        } else {
          localStorage.removeItem(EMPRESA_NOMBRE_KEY);
        }

        if (nextCompany?.sucursalId) {
          localStorage.setItem(SUCURSAL_ID_KEY, nextCompany.sucursalId);
        } else if (!nextCompany?.empresaId) {
          localStorage.removeItem(SUCURSAL_ID_KEY);
          localStorage.removeItem(SUCURSAL_NOMBRE_KEY);
        }
      } catch {
        //
      }

      if (nextToken) httpClient.setToken(nextToken);
      else httpClient.clearToken();

      if (nextRefreshToken) {
        httpClient.setRefreshToken(nextRefreshToken);
      }
    },
    []
  );

  const logout = useCallback(() => {
    setToken("");
    setUser(null);
    setEmpresas([]);
    setAccesos([]);

    try {
      localStorage.removeItem(SUCURSAL_NOMBRE_KEY);
    } catch {
      //
    }

    persist("", "", null, [], [], null);
  }, [persist]);

  const applyMe = useCallback(
    (
      meRes,
      currentToken,
      currentRefreshToken = safeGetItem(REFRESH_TOKEN_KEY)
    ) => {
      const nextUser = meRes?.usuario ?? null;
      const nextEmpresas = licensesToEmpresas(meRes?.licenses);
      const nextAccesos = [];
      const nextCompany = resolveCurrentCompany(meRes);

      setUser(nextUser);
      setEmpresas(nextEmpresas);
      setAccesos(nextAccesos);

      persist(
        currentToken || token,
        currentRefreshToken,
        nextUser,
        nextEmpresas,
        nextAccesos,
        nextCompany
      );

      return {
        me: meRes,
        user: nextUser,
        empresas: nextEmpresas,
        accesos: nextAccesos,
        company: nextCompany,
      };
    },
    [persist, token]
  );

  const refreshMe = useCallback(async () => {
    const t = token || safeGetItem(TOKEN_KEY);
    if (!t) return null;

    try {
      const meRes = await AuthApi.getMe();
      applyMe(meRes, t);
      return meRes;
    } catch {
      logout();
      return null;
    }
  }, [token, applyMe, logout]);

  const login = useCallback(
    async (payload) => {
      const res = await AuthApi.login(payload);

      const nextToken = res?.access_token || "";
      const nextRefreshToken = res?.refresh_token || "";

      if (!nextToken) {
        throw new Error("Login OK pero no vino access_token.");
      }

      setToken(nextToken);
      httpClient.setToken(nextToken);

      if (nextRefreshToken) {
        httpClient.setRefreshToken(nextRefreshToken);
      }

      const meRes = res?.me ?? (await AuthApi.getMe());
      const applied = applyMe(meRes, nextToken, nextRefreshToken);

      return {
        token: nextToken,
        refreshToken: nextRefreshToken,
        ...applied,
        raw: res,
      };
    },
    [applyMe]
  );

  const selectContext = useCallback(
    async ({ licenseId, branchId, employeeRoleId }) => {
      const res = await AuthApi.selectContext({ licenseId, branchId, employeeRoleId });

      const nextToken = res?.access_token || "";
      const nextRefreshToken =
        res?.refresh_token || safeGetItem(REFRESH_TOKEN_KEY);

      if (!nextToken) {
        throw new Error("selectContext OK pero no vino access_token.");
      }

      setToken(nextToken);
      httpClient.setToken(nextToken);

      if (nextRefreshToken) {
        httpClient.setRefreshToken(nextRefreshToken);
      }

      const meRes = res?.me ?? (await AuthApi.getMe());
      const applied = applyMe(meRes, nextToken, nextRefreshToken);

      return {
        token: nextToken,
        refreshToken: nextRefreshToken,
        ...applied,
        raw: res,
      };
    },
    [applyMe]
  );

  useEffect(() => {
    (async () => {
      if (token) {
        httpClient.setToken(token);
        await refreshMe();
      }
      setLoading(false);
    })();
  }, [token, refreshMe]);

  const value = useMemo(
    () => ({
      token,
      user,
      empresas,
      accesos,
      loading,
      isAuthenticated: !!token,
      login,
      logout,
      refreshMe,
      selectContext,
      setUser,
    }),
    [token, user, empresas, accesos, loading, login, logout, refreshMe, selectContext]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de <AuthProvider />");
  return ctx;
}